/**
 * Media-element path (W1-14, A§8): the music plays through an HTMLAudioElement routed into the graph by a
 * MediaElementAudioSourceNode. Used for songs longer than 20 minutes (never decoded to PCM, which would take
 * hundreds of MB) and for pitch-preserving rate (rate != 1 with "keep pitch": `preservesPitch = true`).
 *
 * The element cannot be started sample-accurately, so its clock is MEASURED: every scheduler tick reads
 * `element.currentTime`; a fresh value is a sample `{ctx, pos}` that the transport uses to anchor (a provisional
 * segment) or re-anchor (smoothed drift beyond 10 ms) the timeline. After a start, seek, loop wrap or stall the
 * element keeps reporting its start position until its clock actually runs, so such a reading is not a sample: the
 * first sample is the first reading that MOVED (or, should the clock never move, the reading after 0.5 s). The first
 * sample after every discontinuity is flagged `restarted`; the transport then averages a few samples and removes the
 * residual offset (transport.ts). Sync accuracy is therefore reduced to about +-10 ms (`MusicInfo.streaming` lets the
 * transport bar show it). Loop wraps, rate changes and the song end are performed by seeking / re-rating / pausing
 * the element at the predicted context time (timer plus tick check).
 */

import type { BeginKind, ClockSample, EndKind, MusicOutput } from './music';
import { isProvisional, type Segment } from './timeline';

/** Songs longer than this play through the media element (A§8). */
export const STREAMING_MIN_DURATION_SEC = 20 * 60;

/**
 * Files from this size on get their duration probed (media-element metadata) before decoding, so a song longer
 * than 20 minutes never becomes PCM (25 min of 48 kHz stereo float is about 576 MB). The bound comes from the
 * LOWEST realistic bitrate: 20 min is 4.8 MB at 32 kbit/s (low-bitrate Opus or HE-AAC) and 9.6 MB at 64 kbit/s, so
 * a file under 4 MiB (20 min at under 28 kbit/s) is never that long in practice and is decoded directly. A smaller
 * file that still decodes to more than 20 minutes drops its PCM and streams (AudioEngine.loadMusic).
 */
export const STREAMING_PROBE_MIN_BYTES = 4 * 1024 * 1024;

/** HTMLMediaElement.HAVE_FUTURE_DATA */
const HAVE_FUTURE_DATA = 3;
/** A reading within this of the start position has not moved (s). */
const MOVE_EPSILON_SEC = 1e-6;
/** An element clock that has not moved after this long (context s, from the first ready reading) is sampled anyway. */
export const MOVE_TIMEOUT_SEC = 0.5;

/** The HTMLAudioElement surface the engine uses (an interface so tests can supply fakes). */
export interface MediaElementLike {
  src: string;
  preload: string;
  currentTime: number;
  playbackRate: number;
  preservesPitch: boolean;
  readonly paused: boolean;
  readonly seeking: boolean;
  readonly duration: number;
  readonly readyState: number;
  play(): Promise<void>;
  pause(): void;
  load(): void;
  removeAttribute(name: string): void;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface Timers {
  setTimeout(cb: () => void, ms: number): unknown;
  clearTimeout(id: unknown): void;
}

export const defaultTimers: Timers = {
  setTimeout: (cb, ms) => setTimeout(cb, ms),
  clearTimeout: (id) => {
    clearTimeout(id as ReturnType<typeof setTimeout>);
  },
};

/** Sets preservesPitch and its legacy prefixed forms (older Safari / Firefox). */
export function setPreservesPitch(el: MediaElementLike, on: boolean): void {
  el.preservesPitch = on;
  const legacy = el as unknown as Record<string, unknown>;
  if ('webkitPreservesPitch' in legacy) legacy.webkitPreservesPitch = on;
  if ('mozPreservesPitch' in legacy) legacy.mozPreservesPitch = on;
}

/**
 * Loads `url` into `el` and resolves with its duration once metadata is known (null on error, on an unknown or
 * infinite duration, or after `timeoutMs`).
 */
export function probeDuration(
  el: MediaElementLike,
  url: string,
  timeoutMs = 4000,
  timers: Timers = defaultTimers,
): Promise<number | null> {
  return new Promise<number | null>((resolve) => {
    let done = false;
    let timer: unknown = null;
    const finish = (value: number | null): void => {
      if (done) return;
      done = true;
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('durationchange', onMeta);
      el.removeEventListener('error', onError);
      if (timer !== null) timers.clearTimeout(timer);
      resolve(value);
    };
    const onMeta = (): void => {
      const d = el.duration;
      if (Number.isFinite(d) && d > 0) finish(d);
    };
    const onError = (): void => {
      finish(null);
    };
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('durationchange', onMeta);
    el.addEventListener('error', onError);
    timer = timers.setTimeout(() => {
      finish(null);
    }, timeoutMs);
    el.preload = 'metadata';
    el.src = url;
    el.load();
    onMeta();
  });
}

export interface ElementOutputDeps {
  readonly ctx: { readonly currentTime: number };
  readonly element: MediaElementLike;
  /** Whether "keep pitch" applies (rate != 1 and the setting on). */
  readonly preservePitch: () => boolean;
  readonly timers: Timers;
  /** The element started buffering mid-play (its clock stops; the transport holds until it resumes). */
  readonly onStall: () => void;
}

interface PendingAction {
  readonly seg: Segment;
  readonly at: number;
  readonly action: 'start' | 'rate' | 'pause';
  timer: unknown;
}

export class ElementMusicOutput implements MusicOutput {
  readonly kind = 'element' as const;
  private readonly deps: ElementOutputDeps;
  private pending: PendingAction[] = [];
  /** A segment is playing (the element was started and not paused by us since). */
  private playing = false;
  private lastSample = NaN;
  /**
   * The reading at the last start, seek or stall while the element clock has not moved since (NaN once it moved):
   * readings equal to it are the element still waiting to start, not samples of a running clock.
   */
  private startReading = NaN;
  /** Context time of the first ready reading that still showed `startReading` (NaN before it). */
  private waitingSince = NaN;
  /** The next sample is the first one after a discontinuity of the element clock. */
  private restarted = false;
  private readonly onWaiting = (): void => {
    if (!this.playing) return;
    // Buffering: the clock stops here and resumes later; the transport holds until the next MOVING reading.
    this.awaitMove();
    this.deps.onStall();
  };

  constructor(deps: ElementOutputDeps) {
    this.deps = deps;
    deps.element.addEventListener('waiting', this.onWaiting);
  }

  get element(): MediaElementLike {
    return this.deps.element;
  }

  begin(seg: Segment, how: BeginKind): void {
    const el = this.deps.element;
    if (how === 'splice' && !isProvisional(seg)) {
      // The earlier segments end at the splice: their later actions (a song-end pause, a pre-roll start, an older
      // rate change) are superseded by this segment and the transport's new plan.
      this.dropOthersFrom(seg, seg.ctxStart);
    }
    if (how === 'splice' && this.playing && !el.paused) {
      // Same position, new rate: no seek. Holding (not anchored yet): the new rate applies at once; otherwise at the
      // splice time, so the element and the timeline change speed together.
      if (isProvisional(seg)) this.applyRate(seg.rate);
      else this.schedule({ seg, at: seg.ctxStart, action: 'rate', timer: null });
      return;
    }
    const now = this.deps.ctx.currentTime;
    let at = isProvisional(seg) ? now : seg.ctxStart;
    if (!isProvisional(seg) && seg.posStart < 0) at = seg.ctxStart + -seg.posStart / seg.rate;
    if (how === 'start' && !el.paused) el.pause();
    this.schedule({ seg, at, action: 'start', timer: null });
  }

  end(seg: Segment, when: number, how: EndKind): void {
    // A wrap is performed by the next segment's begin (a seek); a splice keeps playing.
    if (how !== 'end') return;
    this.schedule({ seg, at: when, action: 'pause', timer: null });
  }

  /** Drops the pending actions of `seg` (a re-plan replaced it); the element keeps playing. */
  cancel(seg: Segment): void {
    for (const p of this.pending) if (p.seg === seg && p.timer !== null) this.deps.timers.clearTimeout(p.timer);
    this.pending = this.pending.filter((p) => p.seg !== seg);
  }

  halt(): void {
    for (const p of this.pending) if (p.timer !== null) this.deps.timers.clearTimeout(p.timer);
    this.pending = [];
    this.playing = false;
    this.lastSample = NaN;
    this.startReading = NaN;
    this.waitingSince = NaN;
    this.restarted = false;
    this.deps.element.pause();
  }

  observe(now: number): ClockSample | null {
    this.runDue(now);
    const el = this.deps.element;
    if (!this.playing || el.paused || el.seeking || el.readyState < HAVE_FUTURE_DATA) return null;
    const t = el.currentTime;
    if (!Number.isNaN(this.startReading)) {
      if (Math.abs(t - this.startReading) <= MOVE_EPSILON_SEC) {
        // Started (or resumed) but the clock does not run yet: anchoring here would leave the start-up delay as a
        // permanent offset. A clock that never moves is sampled after MOVE_TIMEOUT_SEC (the transport then runs on
        // its prediction and re-anchors once the element moves).
        if (Number.isNaN(this.waitingSince)) this.waitingSince = now;
        if (now - this.waitingSince < MOVE_TIMEOUT_SEC) return null;
      }
      this.startReading = NaN;
      this.waitingSince = NaN;
    } else if (t === this.lastSample) {
      return null;
    }
    this.lastSample = t;
    const restarted = this.restarted;
    this.restarted = false;
    return { ctx: now, pos: t, restarted };
  }

  dispose(): void {
    this.halt();
    this.deps.element.removeEventListener('waiting', this.onWaiting);
  }

  /** Drops the pending actions of segments other than `seg` due at or after context time `from`. */
  private dropOthersFrom(seg: Segment, from: number): void {
    const keep: PendingAction[] = [];
    for (const p of this.pending) {
      if (p.seg !== seg && p.at >= from - 1e-9) {
        if (p.timer !== null) this.deps.timers.clearTimeout(p.timer);
      } else {
        keep.push(p);
      }
    }
    this.pending = keep;
  }

  /** The clock restarts from the current reading: wait until it moves, and flag the next sample. */
  private awaitMove(): void {
    this.startReading = this.deps.element.currentTime;
    this.waitingSince = NaN;
    this.lastSample = NaN;
    this.restarted = true;
  }

  private applyRate(rate: number): void {
    const el = this.deps.element;
    el.playbackRate = rate;
    setPreservesPitch(el, rate !== 1 && this.deps.preservePitch());
  }

  private schedule(p: PendingAction): void {
    this.pending.push(p);
    this.pending.sort((a, b) => a.at - b.at);
    const delayMs = (p.at - this.deps.ctx.currentTime) * 1000;
    if (delayMs <= 1) {
      this.runDue(this.deps.ctx.currentTime);
      return;
    }
    p.timer = this.deps.timers.setTimeout(() => {
      p.timer = null;
      this.runDue(Math.max(this.deps.ctx.currentTime, p.at));
    }, delayMs);
  }

  /** Performs every pending action due at context time `now`. */
  private runDue(now: number): void {
    while (this.pending.length > 0) {
      const p = this.pending[0];
      if (p === undefined || p.at > now + 0.002) break;
      this.pending.shift();
      if (p.timer !== null) this.deps.timers.clearTimeout(p.timer);
      if (p.action === 'pause') {
        this.deps.element.pause();
        this.playing = false;
        continue;
      }
      if (p.action === 'rate') {
        // A splice at the same rate (a loop or offset edit) leaves the element clock alone.
        if (!this.playing || this.deps.element.playbackRate === p.seg.rate) continue;
        this.applyRate(p.seg.rate);
        this.restarted = true;
        continue;
      }
      this.perform(p.seg, now);
    }
  }

  private perform(seg: Segment, now: number): void {
    const el = this.deps.element;
    // Executing late (timer jitter): start where the segment is by now.
    const late = isProvisional(seg) ? 0 : Math.max(0, now - seg.ctxStart) * seg.rate;
    const pos = Math.max(0, seg.posStart + late);
    this.applyRate(seg.rate);
    if (Math.abs(el.currentTime - pos) > 0.001) el.currentTime = pos;
    this.playing = true;
    // Read back after the seek (the element may round the position): the clock runs once the reading leaves it.
    this.awaitMove();
    if (el.paused) {
      el.play().catch(() => {
        // Autoplay refusal or a decode error: the transport stays on its predicted clock.
      });
    }
  }
}
